import { colorsNew, errorColor, ltSmMediaQuery } from 'root/src/client/web/commonStyles'
import prop from 'ramda/src/prop'
import map from 'ramda/src/map'
import is from 'ramda/src/is'
import Typography, { typographyVariants } from 'root/src/client/web/typography/Typography'
import withStyles from '@material-ui/core/styles/withStyles'
import { memo, isValidElement } from 'react'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import classNames from 'classnames'
import Icon from 'root/src/client/web/base/Icon'
import Toggle from 'root/src/client/web/base/Toggle'
import TooltipWrapper from 'root/src/client/web/base/TooltipWraper'

const styles = {
	accordion: {
		margin: [[7, 0]],
		width: 942,
		display: 'flex',
		flexDirection: 'column',
	},
	accordionFieldSections: {
		backgroundColor: colorsNew.light4,
		borderRadius: 7,
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		cursor: 'pointer',
		userSelect: 'none',
		transition: '0.125s background-color, 0.050s padding',
		boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.34);',
		'&:hover': {
			backgroundColor: colorsNew.dark1,
		},
	},
	accordionSpace: {
		margin: 16,
	},
	accordionIcon: {
		transition: '0.125s',
		transformOrigin: 'center',
	},
	accordionIconWrapper: {
		background: colorsNew.dark2,
		borderRadius: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		width: 42,
		height: 42,
		margin: [[0, 10]],
	},
	iconExpanded: {
		transform: 'rotate(-180deg)',
	},
	accordionActive: {
		backgroundColor: colorsNew.dark1,
		borderRadius: [[7, 7, 0, 0]],
	},
	accordionExpanded: {
		padding: [[20, 20]],
		transform: 'initial',
	},
	accordionCollapsed: {
		maxHeight: 0,
		opacity: 0,
		visibility: 'hidden',
		padding: 0,
		transform: 'scaleY(-1)',
	},
	accordionContent: {
		transition: '0.125s transform, 0.125s opacity',
		borderRadius: [[0, 0, 7, 7]],
		backgroundColor: colorsNew.light4,
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
	},
	accordionHeader: {
		marginBottom: 35,
		textAlign: 'center',
	},
	accordionError: {
		background: errorColor,
		'&:hover': {
			backgroundColor: errorColor,
		},
	},
	label: {
		fontFamily: 'Roboto',
		fontWeight: 700,
		color: colorsNew.light3,
		textTransform: 'uppercase',
		fontSize: 12,
	},
	accordionHeading: {
		margin: [[5, 10]],
		maxWidth: '90%',
		[ltSmMediaQuery]: {
			width: '80%',
		},
	},
	toggleLabel: {
		marginBottom: 5,
	},
	title: {
		padding: 10,
	},
	toggleContainer: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-around',
		alignItems: 'center',
		marginLeft: 'auto',
	},
	toggleRow: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'space-around',
		width: '100%',
	},
}

const TitleComponent = ({ title, classes }) => (
	isValidElement(title)
		?	title
		: (
			<div className={classes.title}>
				<Typography variant={typographyVariants.heading2}>{title}</Typography>
			</div>
		)
)

const AccordionHeader = ({
	classes,
	title,
	fieldSectionsHeaders,
}) => {
	const header = prop(title, fieldSectionsHeaders)
	return header ? (
		<div className={classes.accordionHeader}>
			{map(
				part => (is(Object, part) ? (
					<a href={part.link} key={part.text} target="_blank" style={{ color: colorsNew.teal }} rel="noreferrer">
						<Typography
							variant={typographyVariants.small}
							color={colorsNew.teal}
						>
							{part.text}
						</Typography>
					</a>
				) : (
					<Typography
						key={part}
						variant={typographyVariants.paragraph}
						color={colorsNew.background}
					>
						{part}
					</Typography>
				)),
				header,
			)}
		</div>
	) : null
}

const AccordionToggle = ({ option: { tooltip, label } }) => {
	if (!tooltip) {
		return label
	}

	return (
		<TooltipWrapper tooltipText={tooltip}>
			{label}
		</TooltipWrapper>
	)
}

const Accordion = memo(({
	classes, title, children, icon = faChevronDown, active, setActive, index, tabError,
	fieldSectionsHeaders, subLabel, label, header, onExpand, formToggles, fieldSectionsToggles,
	setInput, moduleKey, ...props
}) => {
	const toggleExpand = () => {
		setActive(active ? -1 : index)
	}
	const showError = !active && tabError && tabError[index]
	const toggleProps = fieldSectionsToggles?.[title]
	return (
		<div
			className={classNames(
				classes.accordion,
			)}
			{...props}
		>
			<div
				className={classNames(
					classes.accordionFieldSections,
					active && classes.accordionActive,
					showError && classes.accordionError,
				)}
				onClick={toggleExpand}
			>
				<div className={classes.accordionHeading}>
					<TitleComponent title={title} classes={classes} />
				</div>
				<div>
					<div style={{ display: 'flex', gap: 20 }}>
						{toggleProps
						&& map((toggle) => {
							const toggleValue = formToggles[toggle.id]
							return (
								<div
									className={classes.toggleContainer}
								>
									<Typography
										className={classes.toggleLabel}
										variant={typographyVariants.mini}
									>
										{toggle.topLabel}
									</Typography>
									<div className={classes.toggleRow}>
										<Typography
											variant={typographyVariants.small}
										>
											<AccordionToggle
												option={toggle.leftOption}
											/>
										</Typography>
										<Toggle
											key={toggle.id}
											checked={toggleValue}
											onChange={(e) => {
												e.stopPropagation()
												setInput(
													!toggleValue,
													moduleKey,
													[toggle.id],
													undefined,
													undefined,
													['form', moduleKey, 'formToggles'],
													toggle.clearFormInputs,
												)
											}}
										/>
										<Typography
											variant={typographyVariants.small}
										>
											<AccordionToggle
												option={toggle.rightOption}
											/>
										</Typography>
									</div>
								</div>
							)
						}, toggleProps)}
						<div
							className={classNames(
								classes.accordionIconWrapper,
							)}
						>
							<Icon
								className={classNames(
									classes.accordionIcon,
									active && classes.iconExpanded,
								)}
								icon={icon}
								size="2x"
								color="#FFF"
							/>
						</div>
					</div>
				</div>
			</div>
			<div
				className={classNames(
					classes.accordionContent,
					active ? classes.accordionExpanded : classes.accordionCollapsed,
				)}
			>
				<AccordionHeader
					title={title}
					fieldSectionsHeaders={fieldSectionsHeaders}
					classes={classes}
					label={label}
					subLabel={subLabel}
				/>
				{children}
			</div>
		</div>
	)
})

export default withStyles(styles, { name: 'MuiAccordion' })(Accordion)
